import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import Link from '../Link';
import useIsClient from '../../hooks/useIsClient';
import { Banner } from '../../models/Banner';
import styles from './styles.module.scss';

type SwiperBreakpoints = Record<number, {
    slidesPerView?: number
    slidesPerGroup?: number
    spaceBetween?: number
    loop?: boolean
}>

type CarouselDeBannersProps = {
    data: Banner[]
    aspectRatioDesktop: string
    aspectRatioMobile: string
    autoplay?: { delay: number } | boolean
    spaceBetween?: number
    slidesPerView?: number
    slidesPerGroup?: number
    loop?: boolean
    navigation?: boolean
    pagination?: { clickable: boolean } | boolean
    // Config de Swiper por ancho mínimo de viewport (mobile-first), reemplaza
    // el viejo patrón de "prop distinta según useMobileHook".
    breakpoints?: SwiperBreakpoints
    // true: el primer slide carga eager (usar solo si es el LCP, ej. el hero).
    // false (default): todos los slides van lazy.
    priority?: boolean
}

const CarouselDeBanners = ({
  data,
  aspectRatioDesktop,
  aspectRatioMobile,
  autoplay,
  spaceBetween,
  slidesPerView,
  slidesPerGroup,
  loop,
  navigation,
  pagination,
  breakpoints,
  priority = false,
}: CarouselDeBannersProps) => {

  // loop + breakpoints resuelve el breakpoint en cliente antes de que
  // termine la hidratación → mismatch. Solo se difiere al cliente cuando
  // hay breakpoints (el hero, sin breakpoints, sigue rendereando en
  // servidor para no perjudicar el LCP).
  const isClient = useIsClient();

  if (!data || data.length === 0) return null;
  if (breakpoints && !isClient) return null;

  return (
    <Swiper
      modules={[Navigation, Pagination, Autoplay]}
      autoplay={autoplay}
      spaceBetween={spaceBetween}
      slidesPerView={slidesPerView}
      slidesPerGroup={slidesPerGroup}
      loop={loop}
      navigation={navigation}
      pagination={pagination}
      breakpoints={breakpoints}
      style={pagination ? { paddingBottom: '32px' } : undefined}
    >
      {data.map((banner, i) => (
        <SwiperSlide key={`banner-${banner.id ?? i}`} style={{ textAlign: 'center' }}>
          <Item
            banner={banner}
            aspectRatioDesktop={aspectRatioDesktop}
            aspectRatioMobile={aspectRatioMobile}
            eager={priority && i === 0}
          />
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

export default CarouselDeBanners;

type ItemProps = {
    banner: Banner
    aspectRatioDesktop: string
    aspectRatioMobile: string
    eager: boolean
}

const Item = ({ banner, aspectRatioDesktop, aspectRatioMobile, eager }: ItemProps) => {

  const desktopImage = banner.attributes.desktop?.data?.attributes;
  const mobileImage = banner.attributes.mobile?.data?.attributes;
  const desktopSrc = desktopImage?.url || mobileImage?.url || '';
  const mobileSrc = mobileImage?.url || desktopImage?.url || '';
  const alt = desktopImage?.alternativeText || mobileImage?.alternativeText || 'We Celebrate';
  const href = banner.attributes.link;
  const external = !!banner.attributes.targetBlank;

  const picture = (
    <picture style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
      {mobileSrc !== desktopSrc && (
        <source media="(max-width: 768px)" srcSet={mobileSrc} />
      )}
      <img
        src={desktopSrc}
        alt={alt}
        loading={eager ? 'eager' : 'lazy'}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </picture>
  );

  return (
    <div
      className={styles.imageContainer}
      style={{ '--ratio-desktop': aspectRatioDesktop, '--ratio-mobile': aspectRatioMobile } as React.CSSProperties}
    >
      {href ? (
        <Link href={href} external={external} style={{ display: 'block', width: '100%', height: '100%' }}>
          {picture}
        </Link>
      ) : picture}
    </div>
  );
};
